import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ApiError } from "@/services/BaseService";
import { useStartGuestDeletion } from "@/hooks/useDataDeletion";
import {
  DeletionMode,
  DeletionTargetDescriptor,
  DeletionTargetType,
} from "@/types/data-deletion";
import { DeletionConfirmBody } from "./DeletionConfirmBody";
import { DeletionJobStatusView } from "./DeletionJobStatusView";

interface AnonymizeGuestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guestId: string;
  /** Guest email, or the guest ID if they have no account — the confirm token. */
  confirmValue: string;
  /** Fires once the anonymise job is accepted (202) — e.g. optimistic list refresh. */
  onStarted?: () => void;
  /** Fires once the job reaches COMPLETED — refresh the now-anonymised guest. */
  onCompleted?: () => void;
}

/**
 * HOTEL_ADMIN anonymise-guest flow: confirm → start `POST /guests/:guestId`
 * (ANONYMIZE, own hotel) → poll the returned job to terminal. `tenantId` is
 * omitted; the backend derives it from the token. Hotel admins may read their own
 * hotel's guest jobs but cannot cancel, so the status view is shown with
 * `canCancel={false}`.
 */
export const AnonymizeGuestDialog: React.FC<AnonymizeGuestDialogProps> = ({
  open,
  onOpenChange,
  guestId,
  confirmValue,
  onStarted,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const startGuest = useStartGuestDeletion();
  const [phase, setPhase] = useState<"confirm" | "status">("confirm");
  const [jobId, setJobId] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const descriptor: DeletionTargetDescriptor = {
    type: DeletionTargetType.GUEST,
    id: guestId,
    label: confirmValue,
    expectedConfirmation: confirmValue,
    mode: DeletionMode.ANONYMIZE,
  };

  const close = () => {
    setPhase("confirm");
    setJobId(null);
    setServerError(null);
    onOpenChange(false);
  };

  const handleConfirm = async (confirmation: string) => {
    try {
      const job = await startGuest.mutateAsync({
        id: guestId,
        dto: { confirmation, mode: DeletionMode.ANONYMIZE },
      });
      toast.success(t("dataDeletion.anonymizeGuest.started"));
      onStarted?.();
      setServerError(null);
      setJobId(job.id);
      setPhase("status");
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 409) {
        toast.error(
          t("dataDeletion.job.toast.inFlight", {
            target: t("dataDeletion.targetWord.GUEST"),
          }),
        );
        close();
        return;
      }
      if (error instanceof ApiError && error.statusCode === 404) {
        toast.error(t("dataDeletion.job.toast.notFound"));
        close();
        return;
      }
      setServerError(
        error instanceof Error
          ? error.message
          : t("dataDeletion.confirm.mismatch", {
              what: t("dataDeletion.confirm.what.email"),
            }),
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          close();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        {phase === "confirm" ? (
          <DeletionConfirmBody
            descriptor={descriptor}
            isPending={startGuest.isPending}
            serverError={serverError}
            onConfirm={(value) => void handleConfirm(value)}
            onCancel={close}
          />
        ) : null}

        {phase === "status" && jobId ? (
          <DeletionJobStatusView
            key={jobId}
            jobId={jobId}
            canCancel={false}
            onCompleted={onCompleted}
            onClose={close}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
